import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import MaterialIcon from "./MaterialIcon";
import { useDismiss } from "./useDismiss";

interface Props {
  dock: HTMLElement | null;
  label: string;
  summary: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

// A pill in the top bar's dock with a drawer that drops down under the
// header. Escape or a click outside closes it; give it a `key` to reset
// the open state when its subject changes.
export default function DockWidget({ dock, label, summary, actions, children }: Props) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const drawer = useRef<HTMLDivElement>(null);
  // The drawer centres under its pill; a pill near the screen edge would push
  // it off-screen, so it's nudged back in by however much it overflows.
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    if (!open || !drawer.current) {
      setShift(0);
      return;
    }
    const margin = 8;
    const rect = drawer.current.getBoundingClientRect();
    setShift((prev) => {
      const left = rect.left - prev;
      const right = rect.right - prev;
      return left < margin ? margin - left : right > window.innerWidth - margin ? window.innerWidth - margin - right : 0;
    });
  }, [open]);

  useDismiss(open, root, () => setOpen(false));

  if (!dock) return null;
  return createPortal(
    <div className="dock-widget" ref={root}>
      <span className="dock-label muted small">{label}</span>
      <div className="dock-main">
      {summary}
      {actions}
      <button
        type="button"
        className="btn icon-btn"
        aria-expanded={open}
        aria-label={`${open ? "Close" : "Open"} ${label}`}
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        <MaterialIcon name={open ? "expand_less" : "expand_more"} />
      </button>
      </div>
      {open && (
        <div
          ref={drawer}
          className="panel drawer"
          role="dialog"
          aria-label={label}
          style={shift ? { marginLeft: shift } : undefined}
        >
          {children}
        </div>
      )}
    </div>,
    dock,
  );
}
